/**
 * Admin FX Override editor.
 *
 * Shows live `/fx/rates` alongside any manual overrides. Each row gives the
 * admin an input + Save / Clear buttons. Saving immediately propagates to
 * the storefront — `_fx_cache` is invalidated server-side so the next call
 * to `/fx/rates` returns the new value.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Save, X, RefreshCw } from "lucide-react";

const FxOverrideEditor = () => {
  const [supported, setSupported] = useState([]);
  const [rates, setRates] = useState({});           // current served rates (live or overridden)
  const [overrides, setOverrides] = useState({});   // currency → manual rate
  const [drafts, setDrafts] = useState({});         // per-row text input value
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: fx }, { data: ov }] = await Promise.all([
        api.get("/fx/rates"),
        api.get("/admin/fx/overrides"),
      ]);
      setSupported(fx?.supported || []);
      setRates(fx?.rates || {});
      const oMap = {};
      (ov?.overrides || []).forEach((r) => { oMap[r.currency] = r.rate; });
      setOverrides(oMap);
      const dMap = {};
      (fx?.supported || []).forEach((c) => { dMap[c] = oMap[c] != null ? String(oMap[c]) : ""; });
      setDrafts(dMap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load FX data");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (currency) => {
    const raw = (drafts[currency] || "").trim();
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a positive number"); return;
    }
    try {
      await api.put(`/admin/fx/overrides/${currency}`, { rate: n });
      toast.success(`${currency} override saved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const clear = async (currency) => {
    if (!window.confirm(`Clear manual override for ${currency} and use live rate?`)) return;
    try {
      await api.delete(`/admin/fx/overrides/${currency}`);
      toast.success(`${currency} restored to live rate`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };

  return (
    <div data-testid="admin-fx-overrides" className="space-y-6 pb-32">
      <div className="flex items-center justify-between">
        <p className="text-white/55 text-sm font-light max-w-xl leading-relaxed">
          Pin a manual rate to insulate the storefront from short-term FX swings. Cleared overrides instantly return to the live rate provider.
        </p>
        <button onClick={load} className="border border-white/30 hover:border-gold text-white hover:text-gold px-5 py-2.5 font-accent text-[10px] tracking-[0.22em] uppercase">
          <RefreshCw size={12} className="inline mr-2" /> Reload
        </button>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-onyx font-accent text-[10px] tracking-[0.22em] uppercase text-white/45">
          <div className="col-span-2">Currency</div>
          <div className="col-span-3">Live Rate (USD →)</div>
          <div className="col-span-3">Manual Override</div>
          <div className="col-span-2">Effective</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {supported.map((cur) => {
          const live = rates[cur];
          const override = overrides[cur];
          const effective = override != null ? override : live;
          return (
            <div key={cur} data-testid={`fx-row-${cur}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 items-center">
              <div className="col-span-2 font-accent text-[11px] tracking-[0.28em] uppercase text-gold">{cur}</div>
              <div className="col-span-3 text-white/70 text-sm">{live != null ? live : "—"}</div>
              <div className="col-span-3">
                <input
                  data-testid={`fx-input-${cur}`}
                  type="number"
                  step="0.0001"
                  placeholder="e.g. 15850"
                  value={drafts[cur] || ""}
                  onChange={(e) => setDrafts({ ...drafts, [cur]: e.target.value })}
                  className="w-full bg-transparent border-b border-white/15 hover:border-gold/50 focus:border-gold focus:outline-none px-2 py-2 text-white text-sm"
                />
              </div>
              <div className="col-span-2 text-ivory text-sm">
                {effective != null ? effective.toLocaleString() : "—"}
                {override != null && <span className="ml-2 font-accent text-[9px] tracking-[0.22em] uppercase bg-gold/20 text-gold px-2 py-0.5">Manual</span>}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <button
                  onClick={() => save(cur)}
                  data-testid={`fx-save-${cur}`}
                  disabled={busy}
                  className="border border-gold/40 text-gold hover:bg-gold hover:text-noir transition-colors px-3 py-2 font-accent text-[10px] tracking-[0.22em] uppercase"
                ><Save size={12} className="inline mr-1" /> Save</button>
                <button
                  onClick={() => clear(cur)}
                  data-testid={`fx-clear-${cur}`}
                  disabled={override == null || busy}
                  className="border border-white/15 hover:border-red-500 text-white/70 hover:text-red-500 transition-colors px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed font-accent text-[10px] tracking-[0.22em] uppercase"
                ><X size={12} className="inline" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FxOverrideEditor;
